/*
 * Looping peeks for sections/slideshow.liquid in "with hints" mode (setting: loop_hints).
 *
 * Puts a copy of the last slide before the first and a copy of the first after the last,
 * so the first slide shows the last one peeking on its left (and vice versa). When a
 * swipe comes to rest on a copy, the scroller jumps instantly to the identical real
 * slide, so the loop is seamless. All positioning is computed from the measured layout
 * (centre the target slide), never as a relative nudge.
 *
 * Copies carry no `ref`, `slide-id` or ids, so slideshow.js never counts them (dots,
 * current index, arrows are unchanged); they are aria-hidden and inert.
 *
 * Rendered as <umikaze-slideshow-loop hidden> next to the slideshow so it is set up and
 * torn down with the section (including theme editor re-renders).
 */
if (!customElements.get('umikaze-slideshow-loop')) {
  class UmikazeSlideshowLoop extends HTMLElement {
    /** @type {HTMLElement[]} */
    #clones = [];
    /** @type {AbortController | undefined} */
    #abort;
    /** @type {number | undefined} */
    #settleTimer;
    /** @type {HTMLElement | undefined} */
    #component;
    /* True while a scroll started by the select() override is still moving */
    #moving = false;
    /** @type {number | undefined} */
    #movingTimer;

    connectedCallback() {
      const scroller = this.parentElement?.querySelector('slideshow-component slideshow-slides');
      if (!(scroller instanceof HTMLElement)) return;

      const slides = [...scroller.children].filter(
        (child) => child instanceof HTMLElement && child.matches('slideshow-slide[ref]')
      );
      if (slides.length < 2) return;

      const first = /** @type {HTMLElement} */ (slides[0]);
      const last = /** @type {HTMLElement} */ (slides[slides.length - 1]);

      const cloneOfLast = this.#makeClone(last, slides.length - 1);
      const cloneOfFirst = this.#makeClone(first, 0);
      scroller.prepend(cloneOfLast);
      scroller.append(cloneOfFirst);
      this.#clones = [cloneOfLast, cloneOfFirst];
      /* Start each copy's Ken Burns motion in step with its slide so the swap is invisible */
      this.#matchAnimations(last, cloneOfLast);
      this.#matchAnimations(first, cloneOfFirst);

      const targets = new Map([
        [cloneOfLast, last],
        [cloneOfFirst, first],
      ]);

      /*
       * The copy in front shifts the real slides along. Centre the real first slide from
       * the measured layout (an absolute position, not a relative nudge), so any scroll
       * correction the browser or slideshow.js applies around the insertion cannot push
       * the start onto the second slide. Re-applied after the next frame, once
       * slideshow.js and the layout have settled, unless the viewer has already moved.
       */
      this.#centre(scroller, first, slides);
      const initialScroll = scroller.scrollLeft;
      requestAnimationFrame(() => {
        if (this.isConnected && Math.abs(scroller.scrollLeft - initialScroll) < 2) {
          this.#centre(scroller, first, slides);
        }
      });

      this.#abort = new AbortController();
      const { signal } = this.#abort;

      /*
       * Moving a <video> in the DOM pauses it, and autoplay does not restart it. Looping
       * makes slideshow.js's wrap-around (which temporarily moves real slides) reachable
       * from the first slide, so restart muted autoplay videos once a change settles.
       */
      const resumeVideos = () => {
        for (const video of scroller.querySelectorAll('video[autoplay][muted]')) {
          if (!(video instanceof HTMLVideoElement) || !video.paused) continue;
          /* Cloned videos may not inherit the muted state from the attribute; autoplay needs it */
          video.muted = true;
          video.play().catch(() => {});
        }
      };

      /*
       * slideshow.js derives `current` on scroll from its IntersectionObserver's visible
       * slides, which can still be stale right after the instant swap, leaving `current`
       * (dots, arrows, next/previous) on the old slide. Once observers have run, align it
       * with the slide actually in the centre through slideshow.js's own instant select.
       */
      const syncCurrent = () => {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            const component = scroller.closest('slideshow-component');
            if (!this.isConnected || this.#moving || !component) return;
            /* A competing scroll can come to rest on a copy after settle() ran; swap it now */
            const resting = this.#centredSlide(scroller);
            const target = resting && targets.get(resting);
            if (resting && target) {
              this.#matchAnimations(resting, target);
              this.#centre(scroller, target, slides);
            }
            const index = slides.indexOf(/** @type {HTMLElement} */ (target ?? resting));
            if (index < 0) return;
            if (Number(/** @type {any} */ (component).current) === index) return;
            customElements
              .get('slideshow-component')
              ?.prototype.select.call(component, index, undefined, { animate: false });
          })
        );
      };

      const settle = () => {
        this.#endMove();
        const resting = this.#centredSlide(scroller);
        const target = resting && targets.get(resting);
        if (resting && target) {
          this.#matchAnimations(resting, target);
          this.#centre(scroller, target, slides);
        }
        syncCurrent();
        resumeVideos();
      };

      /*
       * slideshow.js handles a wrap-around (last -> first, first -> last) and any jump of
       * more than one slide by moving the target slide next to the current one in the DOM
       * and back. Moving an element restarts its CSS animations, so the Ken Burns zoom
       * visibly jumped. With the copies in place, scroll there instead: a wrap goes to the
       * copy beside the current slide (settle() then swaps in the real slide), and a longer
       * jump scrolls straight to the target. No real slide is moved.
       *
       * slideshow.js only learns the new `current` from the scroll once it lands, so a
       * select() made mid-move would be judged against the old slide. Like slideshow.js's
       * own lock during its reorder, ignore selects until the move settles.
       */
      const component = scroller.closest('slideshow-component');
      if (component instanceof HTMLElement) {
        this.#component = component;
        /* An own property shadows the class method; resolve the original at call time so
           this also works if slideshow-component upgrades after this element connects */
        Object.defineProperty(component, 'select', {
          configurable: true,
          writable: true,
          /** @param {unknown} input @param {Event} [event] @param {{ animate?: boolean }} [options] */
          value: (input, event, options = {}) => {
            if (this.#moving) {
              event?.preventDefault();
              return;
            }

            const select = customElements.get('slideshow-component')?.prototype.select;
            const destination = this.#loopDestination(component, slides, input, options);
            if (!destination || component.getAttribute('disabled') === 'true') {
              return select?.call(component, input, event, options);
            }

            event?.preventDefault();
            const offset = this.#offsetFromCentre(scroller, destination);
            if (Math.abs(offset) < 1) return;

            this.#moving = true;
            /* Release the lock even if no scroll end is reported (e.g. the scroll was interrupted) */
            clearTimeout(this.#movingTimer);
            this.#movingTimer = window.setTimeout(() => this.#endMove(), 2000);
            scroller.scrollBy({ left: offset, behavior: 'smooth' });
          },
        });
      }

      /* scrollend where supported; a short debounce covers browsers without it */
      scroller.addEventListener('scrollend', settle, { signal });
      scroller.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.#settleTimer);
          this.#settleTimer = window.setTimeout(settle, 150);
        },
        { signal, passive: true }
      );
      scroller.closest('slideshow-component')?.addEventListener('slideshow:select', resumeVideos, { signal });
      resumeVideos();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      clearTimeout(this.#settleTimer);
      this.#endMove();
      for (const clone of this.#clones) clone.remove();
      this.#clones = [];
      /* Drop the instance override so the prototype's select() applies again */
      if (this.#component) delete (/** @type {any} */ (this.#component)).select;
      this.#component = undefined;
    }

    #endMove() {
      this.#moving = false;
      clearTimeout(this.#movingTimer);
    }

    /**
     * Where to scroll for a select() that slideshow.js would otherwise handle by moving
     * slides in the DOM, or null to let slideshow.js handle it.
     * @param {HTMLElement} component @param {HTMLElement[]} slides @param {unknown} input
     * @param {{ animate?: boolean }} options
     * @returns {HTMLElement | null}
     */
    #loopDestination(component, slides, input, options) {
      if (typeof input !== 'number' && typeof input !== 'string') return null;
      const requested = typeof input === 'number' ? input : parseInt(input, 10);
      if (Number.isNaN(requested)) return null;
      if (options.animate === false || matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

      const current = Number(/** @type {any} */ (component).current);
      const lastIndex = slides.length - 1;
      const [cloneOfLast, cloneOfFirst] = this.#clones;

      if (requested < 0 || (requested === lastIndex && current === 0 && lastIndex > 1)) return cloneOfLast ?? null;
      if (requested > lastIndex || (requested === 0 && current === lastIndex && lastIndex > 1)) return cloneOfFirst ?? null;
      if (Math.abs(requested - current) > 1) return slides[requested] ?? null;
      return null;
    }

    /**
     * Copies the Ken Burns animation progress of `from`'s image onto `to`'s image.
     * @param {HTMLElement} from @param {HTMLElement} to
     */
    #matchAnimations(from, to) {
      const source = from.querySelector('.slide__image')?.getAnimations() ?? [];
      const target = to.querySelector('.slide__image')?.getAnimations() ?? [];
      source.forEach((animation, index) => {
        const copy = target[index];
        if (copy && animation.currentTime != null) copy.currentTime = animation.currentTime;
      });
    }

    /** @param {HTMLElement} source @param {number} index */
    #makeClone(source, index) {
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      for (const element of [clone, ...clone.querySelectorAll('*')]) {
        element.removeAttribute('ref');
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
      }
      clone.removeAttribute('slide-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-loop-clone', '');
      clone.inert = true;
      /* Keep the Ken Burns direction of the slide it copies (odd 0-based index = reversed) */
      clone.toggleAttribute('data-ken-burns-reverse', index % 2 === 1);
      return clone;
    }

    /**
     * How far `slide`'s centre is from the scroller's visible centre, measured from the
     * rendered layout (independent of offsetParent and of the current scroll position).
     * @param {HTMLElement} scroller @param {HTMLElement} slide
     */
    #offsetFromCentre(scroller, slide) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return slideRect.left + slideRect.width / 2 - (scrollerRect.left + scroller.clientWidth / 2);
    }

    /** The slide whose centre is closest to the scroller's centre. @param {HTMLElement} scroller */
    #centredSlide(scroller) {
      let best = null;
      let bestDistance = Infinity;
      for (const child of scroller.children) {
        if (!(child instanceof HTMLElement) || !child.matches('slideshow-slide')) continue;
        const distance = Math.abs(this.#offsetFromCentre(scroller, child));
        if (distance < bestDistance) {
          bestDistance = distance;
          best = child;
        }
      }
      return best;
    }

    /**
     * Instantly scrolls so `slide` sits in the centre (where scroll-snap centres it).
     * @param {HTMLElement} scroller @param {HTMLElement} slide @param {HTMLElement[]} slides
     */
    #centre(scroller, slide, slides) {
      const behavior = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollLeft += this.#offsetFromCentre(scroller, slide);
      scroller.style.scrollBehavior = behavior;

      /* Keep slideshow.js's visible-slide state in step when it does not observe the copy */
      for (const real of slides) real.setAttribute('aria-hidden', String(real !== slide));
    }
  }

  customElements.define('umikaze-slideshow-loop', UmikazeSlideshowLoop);
}
